import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useCollectionBinding, useGallery, useSlotGroup } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import type { PhotoGallery } from '@vibesites/schema';
import { useScrollReveal, useStaggerReveal } from '@vibesites/theme-preferred/hooks/useScrollReveal';
import { useRevealStyles } from '@vibesites/theme-preferred/hooks/useRevealStyles';
import { GlowTrace } from '@vibesites/theme-preferred/components/GlowTrace';

interface Floorplan {
  floorplan_id?: string | null;
  floorplan_name?: string | null;
  floorplan_image_url?: string | null;
  floorplan_beds_min?: string | null;
  floorplan_beds_max?: string | null;
  floorplan_baths_min?: string | null;
  floorplan_sqft_min?: string | null;
  floorplan_sqft_max?: string | null;
  floorplan_price_min?: string | null;
  floorplan_price_max?: string | null;
  floorplan_available_units?: string | null;
}

// ============================================================================
// Bedroom category helpers
// ============================================================================

interface BedroomCategory {
  key: string;
  label: string;
  subtitle: string;
  param: string;
  count: number;
  priceRange: string;
}

const BED_ORDER = ['studio', 'one', 'two', 'three', 'four', 'five'];

function bedCountToKey(bedsRaw: string | null | undefined): string {
  if (!bedsRaw) return 'studio';
  const trimmed = bedsRaw.trim().toLowerCase();
  if (trimmed === '0' || trimmed === 'studio' || trimmed === 'std') return 'studio';
  const num = parseInt(trimmed, 10);
  if (isNaN(num) || num <= 0) return 'studio';
  const names = ['studio', 'one', 'two', 'three', 'four', 'five'];
  return names[num] || `${num}`;
}

function keyToLabel(key: string): string {
  const labels: Record<string, string> = {
    studio: 'Studio',
    one: '1 Bedroom',
    two: '2 Bedroom',
    three: '3 Bedroom',
    four: '4 Bedroom',
    five: '5 Bedroom',
  };
  return labels[key] || `${key} Bedroom`;
}

function keyToShortLabel(key: string): string {
  const labels: Record<string, string> = {
    studio: 'Studio',
    one: '1 Bed',
    two: '2 Bed',
    three: '3 Bed',
    four: '4 Bed',
    five: '5 Bed',
  };
  return labels[key] || `${key} Bed`;
}

function formatPriceRange(plans: Floorplan[]): string {
  const prices = plans
    .flatMap((p) => [p.floorplan_price_min, p.floorplan_price_max])
    .map((v) => (v ? parseInt(String(v), 10) : NaN))
    .filter((v) => !isNaN(v) && v > 0);
  if (!prices.length) return 'Call for Pricing';
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  if (min === max) return `From $${min.toLocaleString()}`;
  return `$${min.toLocaleString()} - $${max.toLocaleString()}`;
}

function groupFloorplansByBeds(floorplans: Floorplan[]): BedroomCategory[] {
  const groups: Record<string, Floorplan[]> = {};

  for (const plan of floorplans) {
    const key = bedCountToKey(plan.floorplan_beds_min);
    if (!groups[key]) groups[key] = [];
    groups[key].push(plan);
  }

  const sorted = Object.keys(groups).sort((a, b) => {
    const ai = BED_ORDER.indexOf(a);
    const bi = BED_ORDER.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  return sorted.map((key) => {
    const plans = groups[key];
    const count = plans.reduce((sum, p) => {
      const units = p.floorplan_available_units ? parseInt(String(p.floorplan_available_units), 10) : 0;
      return sum + (isNaN(units) ? 0 : units);
    }, 0);
    return {
      key,
      label: keyToLabel(key),
      subtitle: keyToShortLabel(key),
      param: key,
      count,
      priceRange: formatPriceRange(plans),
    };
  });
}

// ============================================================================
// Interior photo selection from gallery
// ============================================================================

const INTERIOR_KEYWORDS = [
  'interior', 'apartment', 'unit', 'bedroom', 'living room', 'living',
  'kitchen', 'bathroom', 'bath', 'inside', 'home', 'den', 'dining',
  'closet', 'counter', 'cabinet', 'window', 'floor', 'carpet',
  'hardwood', 'granite', 'stainless', 'appliance', 'washer', 'dryer',
];

function scoreInterior(photo: PhotoGallery): number {
  const text = [photo.title, photo.description, photo.alt_text]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (!text) return 0;
  return INTERIOR_KEYWORDS.reduce((s, kw) => s + (text.includes(kw) ? 1 : 0), 0);
}

function normalizeUrl(url: string): string {
  try {
    const u = new URL(url);
    return (u.origin + u.pathname).replace(/\/+$/, '').toLowerCase();
  } catch {
    return url.toLowerCase().split('?')[0].replace(/\/+$/, '');
  }
}

function pickInteriorPhotos(gallery: PhotoGallery[], count: number): string[] {
  if (!gallery.length) return [];

  const seen = new Set<string>();
  const unique: { photo: PhotoGallery; score: number }[] = [];
  for (const photo of gallery) {
    if (!photo.image) continue;
    const key = normalizeUrl(photo.image);
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push({ photo, score: scoreInterior(photo) });
  }

  unique.sort((a, b) => b.score - a.score);

  const pool = unique.slice(0, Math.max(count * 2, 8));
  if (pool.length <= count) {
    return pool.map((p) => p.photo.image!);
  }

  const step = pool.length / count;
  const picked: string[] = [];
  for (let i = 0; i < count; i++) {
    const idx = Math.min(Math.floor(i * step), pool.length - 1);
    picked.push(pool[idx].photo.image!);
  }
  return picked;
}

// ============================================================================
// Image slot group definitions
// ============================================================================

function makeImageDef(label: string, index: number, defaultAlt: string): SlotGroupDefinition {
  return {
    label,
    fields: {
      image: {
        label: 'Image',
        type: 'image',
        defaultField: `photo_gallery[${index}].image`,
      },
      alt: {
        label: 'Alt Text',
        type: 'text',
        defaultField: `photo_gallery[${index}].alt_text`,
        defaultValue: defaultAlt,
      },
    },
    sourceBinding: {
      collection: 'photo_gallery',
      defaultIndex: index,
      fieldMappings: { image: 'image', alt: 'alt_text' },
    },
  };
}

// Default sizes for denser grids (4–6 cards). Narrower layouts override
// via `cardImageSizes` based on `totalCards`.
const CARD_IMAGE_SIZES =
  '(min-width: 1280px) 17vw, (min-width: 1024px) 20vw, (min-width: 768px) 34vw, (min-width: 640px) 50vw, 100vw';

const IMAGE_ALL_DEF = makeImageDef('"View All" Card Image', 0, 'View all floor plans');
const IMAGE_STUDIO_DEF = makeImageDef('Studio Card Image', 1, 'Studio floor plans');
const IMAGE_ONE_DEF = makeImageDef('1 Bedroom Card Image', 2, '1 Bedroom floor plans');
const IMAGE_TWO_DEF = makeImageDef('2 Bedroom Card Image', 3, '2 Bedroom floor plans');
const IMAGE_THREE_DEF = makeImageDef('3 Bedroom Card Image', 4, '3 Bedroom floor plans');
const IMAGE_FOUR_DEF = makeImageDef('4 Bedroom Card Image', 5, '4 Bedroom floor plans');
const IMAGE_FIVE_DEF = makeImageDef('5 Bedroom Card Image', 6, '5 Bedroom floor plans');

// ============================================================================
// Component
// ============================================================================

export function FloorplansList() {
  const label = useContentToken('floorplanslist.label');

  // Card image slot groups (image + alt). Called unconditionally to satisfy
  // React hook rules; only the ones for rendered cards produce visible output.
  const imageAll = useSlotGroup('floorplanslist.image_all', IMAGE_ALL_DEF);
  const imageStudio = useSlotGroup('floorplanslist.image_studio', IMAGE_STUDIO_DEF);
  const imageOne = useSlotGroup('floorplanslist.image_one', IMAGE_ONE_DEF);
  const imageTwo = useSlotGroup('floorplanslist.image_two', IMAGE_TWO_DEF);
  const imageThree = useSlotGroup('floorplanslist.image_three', IMAGE_THREE_DEF);
  const imageFour = useSlotGroup('floorplanslist.image_four', IMAGE_FOUR_DEF);
  const imageFive = useSlotGroup('floorplanslist.image_five', IMAGE_FIVE_DEF);

  // Collapse ranges whose endpoints match ("1-1" → "1", "3 - 3" → "3") so
  // single-bedroom counts don't read as a redundant range.
  const collapsedLabel = useMemo(() => {
    if (!label) return label;
    const trimmed = label.trim();
    if (!trimmed) return label;
    const parts = trimmed.split(/\s*[-–—]\s*/).map((p) => p.trim());
    if (parts.length > 1 && parts.every((p) => p.toLowerCase() === parts[0].toLowerCase())) {
      return parts[0];
    }
    return parts.join(' - ');
  }, [label]);

  const capitalizedLabel = collapsedLabel
    ? collapsedLabel.replace(/\b\w/g, (c) => c.toUpperCase())
    : null;
  const formattedLabel = capitalizedLabel ? `${capitalizedLabel} Bedroom Layouts` : null;

  const floorplans = useCollectionBinding<Floorplan>('floorplanslist.items', {
    collection: 'floorplans',
  });

  const gallery = useGallery();

  const categories = useMemo(
    () => (floorplans?.length ? groupFloorplansByBeds(floorplans) : []),
    [floorplans],
  );

  const categoriesByKey = useMemo(() => {
    const map: Record<string, BedroomCategory> = {};
    for (const cat of categories) map[cat.key] = cat;
    return map;
  }, [categories]);

  // Smart auto-picked photos serve as the runtime fallback when a slot's
  // resolved image is empty (e.g. gallery is short and the defaultField
  // index is out of range).
  const photosByKey = useMemo(() => {
    const totalCards = categories.length + 1;
    const picks = pickInteriorPhotos(gallery, totalCards);
    const result: Record<string, string | undefined> = { all: picks[0] };
    categories.forEach((cat, i) => {
      result[cat.key] = picks[i + 1];
    });
    return result;
  }, [gallery, categories]);

  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();
  const gridRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 120 });

  const hasCategories = categories.length > 0;
  const totalCards = categories.length + 1;

  // Keep card width/height in a reasonable band when few cards would otherwise
  // stretch across the full max-w-7xl row (portrait aspect makes that huge).
  const { gridCols, gridWrap, cardAspect, cardImageSizes } = useMemo(() => {
    if (totalCards <= 2) {
      return {
        gridCols: 'grid-cols-1 sm:grid-cols-2',
        // Cap the row so each card stays ~similar to a 3–4 column layout card.
        gridWrap: 'max-w-sm sm:max-w-3xl mx-auto w-full',
        // Slightly shorter portrait than the denser grids to keep height in check.
        cardAspect: 'aspect-[4/3] sm:aspect-[4/5]',
        cardImageSizes: '(min-width: 640px) 20rem, 100vw',
      };
    }
    if (totalCards === 3) {
      return {
        gridCols: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3',
        gridWrap: 'w-full',
        cardAspect: 'aspect-[4/3] sm:aspect-[3/4]',
        cardImageSizes: CARD_IMAGE_SIZES,
      };
    }
    if (totalCards === 4) {
      return {
        gridCols: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4',
        gridWrap: 'w-full',
        cardAspect: 'aspect-[4/3] sm:aspect-[3/4]',
        cardImageSizes: CARD_IMAGE_SIZES,
      };
    }
    if (totalCards === 5) {
      return {
        gridCols: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5',
        gridWrap: 'w-full',
        cardAspect: 'aspect-[4/3] sm:aspect-[3/4]',
        cardImageSizes: CARD_IMAGE_SIZES,
      };
    }
    return {
      gridCols: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6',
      gridWrap: 'w-full',
      cardAspect: 'aspect-[4/3] sm:aspect-[3/4]',
      cardImageSizes: CARD_IMAGE_SIZES,
    };
  }, [totalCards]);

  const cardClassName = `reveal-fade-up fp-card glow-trace-wrap group relative block ${cardAspect} overflow-hidden cursor-pointer`;

  // Compute delay index across cards (all + rendered categories) for GlowTrace.
  let delayIndex = 0;

  return (
    <section className="py-16 md:py-24 bg-[var(--vibe-color-primary)]">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
        <div ref={headingRef} className="reveal-fade-up text-center mb-12">
          {formattedLabel && (
            <EditableToken path="floorplanslist.label" defaultField="property_bedrooms_range" as="p" className="fp-text-muted font-body text-base sm:text-lg mb-3">
              {formattedLabel}
            </EditableToken>
          )}
          <RichTextContent
            path="floorplanslist.title"
            label="Title"
            defaultValue="<p>Make Yourself at Home</p>"
            as="div"
            className="fp-text font-heading text-3xl sm:text-5xl font-bold mb-4 tracking-tight [&_p]:m-0 [&_h1]:m-0 [&_h2]:m-0"
          />
          <RichTextContent
            path="floorplanslist.subtitle"
            label="Subtitle"
            defaultValue="<p>Please select your desired layout below.</p>"
            as="div"
            className="fp-text-muted font-body text-base sm:text-lg max-w-2xl mx-auto [&_p]:m-0 [&_h1]:m-0 [&_h2]:m-0"
          />
        </div>

        {hasCategories ? (
          <div ref={gridRef} className={`grid ${gridCols} gap-3 md:gap-4 ${gridWrap}`}>
            {/* "View All" card first */}
            <a
              data-reveal
              href="/floor-plans"
              className={cardClassName}
            >
              <GlowTrace delay={0.3 + (delayIndex++) * 0.25} />
              <EditableSlotGroup groupPath="floorplanslist.image_all" definition={IMAGE_ALL_DEF}>
                <div className="absolute inset-0">
                  {(imageAll.image || photosByKey.all) && (
                    <VibesitesImage
                      url={imageAll.image || photosByKey.all || ''}
                      alt={imageAll.alt || 'View all floor plans'}
                      sizes={cardImageSizes}
                      className="w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-110"
                    />
                  )}
                </div>
              </EditableSlotGroup>
              <div className="absolute inset-0 fp-card-overlay transition-opacity duration-300 group-hover:opacity-90" />
              <div className="relative z-10 flex flex-col items-center justify-center h-full text-center fp-card-text px-4">
                <span className="font-heading text-5xl sm:text-6xl md:text-7xl font-bold leading-none mb-3 drop-shadow-lg">ALL</span>
                <span className="font-body text-[11px] sm:text-xs uppercase tracking-[0.2em] drop-shadow-md">View All Layouts</span>
              </div>
            </a>

            {/* Studio card */}
            {categoriesByKey.studio && (
              <a
                data-reveal
                href="/floor-plans?bedroom=studio"
                className={cardClassName}
              >
                <GlowTrace delay={0.3 + (delayIndex++) * 0.25} />
                <EditableSlotGroup groupPath="floorplanslist.image_studio" definition={IMAGE_STUDIO_DEF}>
                  <div className="absolute inset-0">
                    {(imageStudio.image || photosByKey.studio) && (
                      <VibesitesImage
                        url={imageStudio.image || photosByKey.studio || ''}
                        alt={imageStudio.alt || `${categoriesByKey.studio.label} floor plans`}
                        sizes={cardImageSizes}
                        className="w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-110"
                      />
                    )}
                  </div>
                </EditableSlotGroup>
                <div className="absolute inset-0 fp-card-overlay transition-opacity duration-300 group-hover:opacity-90" />
                <div className="relative z-10 flex flex-col items-center justify-center h-full text-center fp-card-text px-4">
                  <span className="font-heading text-5xl sm:text-6xl md:text-7xl font-bold leading-none mb-3 drop-shadow-lg">S</span>
                  <span className="font-body text-[11px] sm:text-xs uppercase tracking-[0.2em] drop-shadow-md">
                    {categoriesByKey.studio.label}
                  </span>
                </div>
              </a>
            )}

            {/* 1 Bedroom card */}
            {categoriesByKey.one && (
              <a
                data-reveal
                href="/floor-plans?bedroom=one"
                className={cardClassName}
              >
                <GlowTrace delay={0.3 + (delayIndex++) * 0.25} />
                <EditableSlotGroup groupPath="floorplanslist.image_one" definition={IMAGE_ONE_DEF}>
                  <div className="absolute inset-0">
                    {(imageOne.image || photosByKey.one) && (
                      <VibesitesImage
                        url={imageOne.image || photosByKey.one || ''}
                        alt={imageOne.alt || `${categoriesByKey.one.label} floor plans`}
                        sizes={cardImageSizes}
                        className="w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-110"
                      />
                    )}
                  </div>
                </EditableSlotGroup>
                <div className="absolute inset-0 fp-card-overlay transition-opacity duration-300 group-hover:opacity-90" />
                <div className="relative z-10 flex flex-col items-center justify-center h-full text-center fp-card-text px-4">
                  <span className="font-heading text-5xl sm:text-6xl md:text-7xl font-bold leading-none mb-3 drop-shadow-lg">1</span>
                  <span className="font-body text-[11px] sm:text-xs uppercase tracking-[0.2em] drop-shadow-md">
                    {categoriesByKey.one.label} Layouts
                  </span>
                </div>
              </a>
            )}

            {/* 2 Bedroom card */}
            {categoriesByKey.two && (
              <a
                data-reveal
                href="/floor-plans?bedroom=two"
                className={cardClassName}
              >
                <GlowTrace delay={0.3 + (delayIndex++) * 0.25} />
                <EditableSlotGroup groupPath="floorplanslist.image_two" definition={IMAGE_TWO_DEF}>
                  <div className="absolute inset-0">
                    {(imageTwo.image || photosByKey.two) && (
                      <VibesitesImage
                        url={imageTwo.image || photosByKey.two || ''}
                        alt={imageTwo.alt || `${categoriesByKey.two.label} floor plans`}
                        sizes={cardImageSizes}
                        className="w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-110"
                      />
                    )}
                  </div>
                </EditableSlotGroup>
                <div className="absolute inset-0 fp-card-overlay transition-opacity duration-300 group-hover:opacity-90" />
                <div className="relative z-10 flex flex-col items-center justify-center h-full text-center fp-card-text px-4">
                  <span className="font-heading text-5xl sm:text-6xl md:text-7xl font-bold leading-none mb-3 drop-shadow-lg">2</span>
                  <span className="font-body text-[11px] sm:text-xs uppercase tracking-[0.2em] drop-shadow-md">
                    {categoriesByKey.two.label} Layouts
                  </span>
                </div>
              </a>
            )}

            {/* 3 Bedroom card */}
            {categoriesByKey.three && (
              <a
                data-reveal
                href="/floor-plans?bedroom=three"
                className={cardClassName}
              >
                <GlowTrace delay={0.3 + (delayIndex++) * 0.25} />
                <EditableSlotGroup groupPath="floorplanslist.image_three" definition={IMAGE_THREE_DEF}>
                  <div className="absolute inset-0">
                    {(imageThree.image || photosByKey.three) && (
                      <VibesitesImage
                        url={imageThree.image || photosByKey.three || ''}
                        alt={imageThree.alt || `${categoriesByKey.three.label} floor plans`}
                        sizes={cardImageSizes}
                        className="w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-110"
                      />
                    )}
                  </div>
                </EditableSlotGroup>
                <div className="absolute inset-0 fp-card-overlay transition-opacity duration-300 group-hover:opacity-90" />
                <div className="relative z-10 flex flex-col items-center justify-center h-full text-center fp-card-text px-4">
                  <span className="font-heading text-5xl sm:text-6xl md:text-7xl font-bold leading-none mb-3 drop-shadow-lg">3</span>
                  <span className="font-body text-[11px] sm:text-xs uppercase tracking-[0.2em] drop-shadow-md">
                    {categoriesByKey.three.label} Layouts
                  </span>
                </div>
              </a>
            )}

            {/* 4 Bedroom card */}
            {categoriesByKey.four && (
              <a
                data-reveal
                href="/floor-plans?bedroom=four"
                className={cardClassName}
              >
                <GlowTrace delay={0.3 + (delayIndex++) * 0.25} />
                <EditableSlotGroup groupPath="floorplanslist.image_four" definition={IMAGE_FOUR_DEF}>
                  <div className="absolute inset-0">
                    {(imageFour.image || photosByKey.four) && (
                      <VibesitesImage
                        url={imageFour.image || photosByKey.four || ''}
                        alt={imageFour.alt || `${categoriesByKey.four.label} floor plans`}
                        sizes={cardImageSizes}
                        className="w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-110"
                      />
                    )}
                  </div>
                </EditableSlotGroup>
                <div className="absolute inset-0 fp-card-overlay transition-opacity duration-300 group-hover:opacity-90" />
                <div className="relative z-10 flex flex-col items-center justify-center h-full text-center fp-card-text px-4">
                  <span className="font-heading text-5xl sm:text-6xl md:text-7xl font-bold leading-none mb-3 drop-shadow-lg">4</span>
                  <span className="font-body text-[11px] sm:text-xs uppercase tracking-[0.2em] drop-shadow-md">
                    {categoriesByKey.four.label} Layouts
                  </span>
                </div>
              </a>
            )}

            {/* 5 Bedroom card */}
            {categoriesByKey.five && (
              <a
                data-reveal
                href="/floor-plans?bedroom=five"
                className={cardClassName}
              >
                <GlowTrace delay={0.3 + (delayIndex++) * 0.25} />
                <EditableSlotGroup groupPath="floorplanslist.image_five" definition={IMAGE_FIVE_DEF}>
                  <div className="absolute inset-0">
                    {(imageFive.image || photosByKey.five) && (
                      <VibesitesImage
                        url={imageFive.image || photosByKey.five || ''}
                        alt={imageFive.alt || `${categoriesByKey.five.label} floor plans`}
                        sizes={cardImageSizes}
                        className="w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-110"
                      />
                    )}
                  </div>
                </EditableSlotGroup>
                <div className="absolute inset-0 fp-card-overlay transition-opacity duration-300 group-hover:opacity-90" />
                <div className="relative z-10 flex flex-col items-center justify-center h-full text-center fp-card-text px-4">
                  <span className="font-heading text-5xl sm:text-6xl md:text-7xl font-bold leading-none mb-3 drop-shadow-lg">5</span>
                  <span className="font-body text-[11px] sm:text-xs uppercase tracking-[0.2em] drop-shadow-md">
                    {categoriesByKey.five.label} Layouts
                  </span>
                </div>
              </a>
            )}
          </div>
        ) : (
          <div className="text-center py-12 fp-text-subtle">
            <p className="font-body text-lg">Floor plans will be displayed here.</p>
          </div>
        )}
      </div>

      <style>{`
        .fp-text {
          color: var(--vibe-color-text-on-primary, white);
        }
        .fp-text-muted {
          color: color-mix(in srgb, var(--vibe-color-text-on-primary, white) 80%, transparent);
        }
        .fp-text-subtle {
          color: color-mix(in srgb, var(--vibe-color-text-on-primary, white) 60%, transparent);
        }
        .fp-card-text {
          color: var(--vibe-color-text-on-primary, white);
        }
        .fp-card {
          border-radius: 4px;
        }
        .fp-card-overlay {
          background: linear-gradient(
            180deg,
            color-mix(in srgb, var(--vibe-color-primary) 70%, black) 0%,
            color-mix(in srgb, var(--vibe-color-primary) 85%, black) 100%
          );
          opacity: 0.8;
        }
        .fp-card:hover .fp-card-overlay {
          opacity: 0.88;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorplansList',
  displayName: 'Floor Plans List',
  tokens: {
    'floorplanslist.label': {
      label: 'Section Label',
      type: 'text',
      defaultField: 'property_bedrooms_range',
      description: 'Label above the title showing bedroom range (e.g., "1 - 3 Bedroom Layouts").',
      suggestedFields: ['property_bedrooms_range'],
    },
    'floorplanslist.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Make Yourself at Home</p>',
    },
    'floorplanslist.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Please select your desired layout below.</p>',
    },
  },
  slotGroups: {
    'floorplanslist.image_all': {
      label: '"View All" Card Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text', defaultValue: 'View all floor plans' },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 0,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
    'floorplanslist.image_studio': {
      label: 'Studio Card Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text', defaultValue: 'Studio floor plans' },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 1,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
    'floorplanslist.image_one': {
      label: '1 Bedroom Card Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text', defaultValue: '1 Bedroom floor plans' },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 2,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
    'floorplanslist.image_two': {
      label: '2 Bedroom Card Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text', defaultValue: '2 Bedroom floor plans' },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 3,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
    'floorplanslist.image_three': {
      label: '3 Bedroom Card Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[4].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[4].alt_text', defaultValue: '3 Bedroom floor plans' },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 4,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
    'floorplanslist.image_four': {
      label: '4 Bedroom Card Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[5].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[5].alt_text', defaultValue: '4 Bedroom floor plans' },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 5,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
    'floorplanslist.image_five': {
      label: '5 Bedroom Card Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[6].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[6].alt_text', defaultValue: '5 Bedroom floor plans' },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 6,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
  },
  collectionBindings: {
    'floorplanslist.items': {
      label: 'Floor Plans',
      collection: 'floorplans',
    },
  },
};

(FloorplansList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FloorplansList;
